import type { FC } from 'react';

import { useContent, useImageMapper, useParams } from '@eneco/cms';
import {
  ContentContainer,
  HeroContainer,
  HeroContentGrid,
  HeroImage,
  HeroLeftColumn,
  HeroRightColumn,
  RightPlaceholderBox,
} from '@eneco/custom-components/Hero';
import { Relative } from '@eneco/custom-components/Section';
import { Bleed, Box, PageGrid } from '@eneco/sparky';
import type { BoxProps } from '@eneco/sparky/types';
import { Placeholder } from '@sitecore/common';

interface HeroContent {
  image?: unknown;
}

interface HeroParams {
  backgroundColor?: BoxProps['backgroundColor'];
  backgroundColorBottom?: BoxProps['backgroundColor'];
  height?: 'variable';
  width?: 'variable';
  minMargin?: 'true';
}

interface HeroRebrandedInnerProps {
  hasLeftPlaceholder: boolean;
  hasRightPlaceholder: boolean;
  hasBottomPlaceholder: boolean;
}

/**
 * The body of the `HeroRebranded` component. Same model as `HeroInner`:
 * data via `@eneco/cms` hooks, nested placeholder slots still served by the
 * legacy `<Placeholder>` from `@sitecore/common`.
 */
const HeroRebrandedInner: FC<HeroRebrandedInnerProps> = ({
  hasLeftPlaceholder,
  hasRightPlaceholder,
  hasBottomPlaceholder,
}) => {
  const content = useContent<HeroContent>();
  const params = useParams<HeroParams>();
  const mapImage = useImageMapper();

  const isVariableHeight = params?.height === 'variable';
  const isVariableWidth = params?.width === 'variable';
  const isMinMargin = params?.minMargin === 'true';

  const imageProps = mapImage(content.image);

  return (
    <Box
      backgroundColor={params?.backgroundColorBottom}
      borderRadius="m"
      paddingTop={{ initial: '2', xl: '10' }}
      paddingX={{ md: '2', xl: '0' }}>
      <HeroContainer
        backgroundColor={params?.backgroundColor}
        isVariableWidth={isVariableWidth}
        isMinMargin={isMinMargin}>
        {imageProps?.src && (
          <HeroImage
            src={imageProps.src}
            alt={imageProps.alt ?? ''}
            sources={imageProps.sources}
            overlay={<Placeholder name="jss-promo-box" />}
          />
        )}

        <ContentContainer
          alignY="justify"
          hasBottomPlaceholder={hasBottomPlaceholder}
          hasRightPlaceholder={hasRightPlaceholder}
          isVariableHeight={isVariableHeight}>
          <HeroContentGrid
            paddingTop={{ initial: hasRightPlaceholder || isVariableHeight ? '8' : '0', lg: '16' }}
            paddingBottom={{ initial: isVariableHeight ? '24' : '8', lg: hasBottomPlaceholder ? '8' : '16' }}>
            {hasLeftPlaceholder && (
              <HeroLeftColumn>
                <Placeholder name="jss-hero-left" />
              </HeroLeftColumn>
            )}
            {hasRightPlaceholder && (
              <HeroRightColumn>
                <RightPlaceholderBox paddingTop={{ initial: '2', lg: '0' }} paddingBottom={{ initial: '2', xl: '0' }}>
                  <Placeholder name="jss-hero-right" />
                </RightPlaceholderBox>
              </HeroRightColumn>
            )}
          </HeroContentGrid>
        </ContentContainer>
      </HeroContainer>
      {hasBottomPlaceholder && (
        <Relative>
          <Bleed top={{ initial: isVariableHeight ? '24' : '0', lg: isVariableHeight ? undefined : '24' }}>
            <PageGrid>
              <PageGrid.Item gridColumn="1/-1">
                <Placeholder name="jss-hero-bottom" />
              </PageGrid.Item>
            </PageGrid>
          </Bleed>
        </Relative>
      )}
    </Box>
  );
};

export { HeroRebrandedInner };
export default HeroRebrandedInner;
